import { UseQueryResult } from '@tanstack/react-query';
import merge from 'deepmerge';
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import { useDashboardContext } from '../DashboardContext';
import { type Context, type Panel as PanelType } from '../types';
import { useReactQuery } from '../useReactQuery';

export enum PanelMode {
    NORMAL = 'normal',
    SCHEMA = 'schema',
    DATA = 'data',
    LOADING = 'loading',
    NO_DATA = 'noData'
}

export interface PanelState {
    /** Whether the panel is in editing mode. */
    isEditing: boolean;
    /** Whether the panel is in full screen mode. */
    isFullScreen: boolean;
    /** Whether the panel can be edited. */
    canEdit: boolean;
    /** Whether the panel can fetch data. */
    canFetch: boolean;
    /** Whether the panel can be full screen. */
    canFullScreen: boolean;
    /** The current mode of the panel. */
    mode: PanelMode;
    /** Whether the panel has no data. */
    noData: boolean;
    /** height of the children's container of the main content of the card. */
    height?: number | undefined;
    /** ref needed to get the size of the container with this ref. */
    ref: React.RefObject<HTMLDivElement>;
    /** setter for height of the children container of the main content of the card. */
    setHeight: React.Dispatch<React.SetStateAction<number>>;
}

export interface PanelContextValue<TPanel extends PanelType> {
    /** The context of the panel. Used to interpolate the panel properties and data parameters. */
    context: Context;
    /** The panel data. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryResults: UseQueryResult<any, Error>;
    /** The original panel schema. */
    schema: TPanel;
    /** The current panel state. */
    panelState: PanelState;
    /** Set the editing mode of the panel. */
    setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
    /** Set the full screen mode of the panel. */
    setIsFullScreen: React.Dispatch<React.SetStateAction<boolean>>;
    /** Set the mode of the panel. */
    setMode: React.Dispatch<React.SetStateAction<PanelMode>>;
    /** Set the no data mode of the panel. */
    setNoData: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Context for the Dashboard component.
 */
const PanelContext = createContext<PanelContextValue<PanelType> | null>(null);

/**
 * Hook to access the PanelContext.
 * Throws an error if used outside the PanelContextProvider.
 */
export const usePanelContext = <TPanel extends PanelType>() => {
    const context = useContext(PanelContext) as PanelContextValue<TPanel>;

    if (context === null) {
        throw new Error('`usePanelContext` must be used within `PanelContext`');
    }

    return context;
};

/**
 * Provider component for the PanelContext.
 * Supplies dashboard context and utilities to its children.
 */
export const PanelContextProvider = <TPanel extends PanelType>({
    canEdit,
    canFetch,
    canFullScreen,
    children,
    schema
}: Pick<PanelState, 'canEdit' | 'canFetch' | 'canFullScreen'> & {
    /** The children of the panel context provider. */
    children: React.ReactNode;
    /** The schema of the panel. */
    schema: TPanel;
}) => {
    /** Internal panel mode */
    const [mode, setMode] = useState<PanelMode>(PanelMode.NORMAL);
    /** Whether the panel is in editing mode. */
    const [isEditing, setIsEditing] = useState(false);
    /** Whether the panel is in full screen mode. */
    const [isFullScreen, setIsFullScreen] = useState(false);
    /** Whether the panel has no data. */
    const [noData, setNoData] = useState(false);

    const ref = useRef(null);
    const [height, setHeight] = useState(0);

    // Get the dashboard context
    const {
        dashboardState: {
            dataUpdatedAt: dashboardDataUpdatedAt,
            refreshDashboard
        },
        setDataUpdatedAt,
        context: dashboardContext
    } = useDashboardContext();

    // Merge dashboard and panel schema context, panel takes precedence
    const initialContext = useMemo<Context>(
        () => merge(dashboardContext, schema.context || {}),
        [dashboardContext, schema]
    );

    // Merge dashboard and panel schema context, panel takes precedence
    const [context, setContext] = useState(initialContext);

    useEffect(() => {
        // Ensure our context state reflects the latest values by merging when dashboard context updates.
        setContext((prev) => merge<Context>(prev, dashboardContext));
    }, [dashboardContext]);

    // Get the query results
    const queryResults = useReactQuery(schema, context, refreshDashboard);
    const { dataUpdatedAt } = queryResults;

    useEffect(() => {
        if (
            (dataUpdatedAt && dashboardDataUpdatedAt === null) ||
            (dashboardDataUpdatedAt && dataUpdatedAt > dashboardDataUpdatedAt)
        ) {
            setDataUpdatedAt(dataUpdatedAt);
        }
    }, [dataUpdatedAt, dashboardDataUpdatedAt, setDataUpdatedAt]);

    // Allow updating context (e.g., time/variables)
    const updateContext = (updates: Context) =>
        setContext((prev) => ({ ...prev, ...updates }));

    const contextValue = useMemo<PanelContextValue<TPanel>>(
        () => ({
            context,
            panelState: {
                canEdit,
                canFetch,
                canFullScreen,
                height,
                isEditing,
                isFullScreen,
                mode,
                noData,
                ref,
                setHeight
            },
            queryResults,
            schema,
            setIsEditing,
            setIsFullScreen,
            setMode,
            setNoData,
            updateContext
        }),
        [
            canEdit,
            canFetch,
            canFullScreen,
            context,
            height,
            isEditing,
            isFullScreen,
            mode,
            noData,
            queryResults,
            ref,
            schema,
            setHeight
        ]
    );
    return (
        <PanelContext.Provider value={contextValue}>
            {children}
        </PanelContext.Provider>
    );
};
